import { useState, useEffect } from 'react';
import { useApp } from '../../context';
import { parseRoles } from '../../utils/rbac';
import api from '../../hooks/useApi';
import { formatDate } from '../../utils/format';
import Tag from '../shared/Tag';
import Modal from '../shared/Modal';
import { useToast } from '../ui/Toast';
import { leaveDaysFor, UK_BANK_HOLIDAYS_FALLBACK, proposedChange } from '../../utils/leaveDays';
import Avatar from '../shared/Avatar';

// ── Pro-rata calculator (HR only) ────────────────────────────────────────────
function ProRataModal({ people, onClose }) {
  const currentYear = new Date().getFullYear();
  const [personId, setPersonId]     = useState('');
  const [year, setYear]             = useState(`${currentYear}-${String(currentYear + 1).slice(2)}`);
  const [leaveType, setLeaveType]   = useState('annual');
  const [result, setResult]         = useState(null);
  const [saving, setSaving]         = useState(false);
  const [saved, setSaved]           = useState(false);

  // Build year options: current + 2 back + 1 forward
  const yearOptions = [];
  for (let y = currentYear - 2; y <= currentYear + 1; y++) {
    yearOptions.push(`${y}-${String(y + 1).slice(2)}`);
  }

  const handleCalculate = async () => {
    if (!personId || !year) return;
    setSaving(true); setSaved(false);
    try {
      const r = await api.post('holiday-prorata', { person_id: personId, year, leave_type: leaveType });
      setResult(r);
    } catch (e) {
      console.error(e);
    }
    setSaving(false);
  };

  const handleSave = async () => {
    if (!result) return;
    setSaving(true);
    try {
      await api.post('holiday-prorata', { person_id: personId, year, leave_type: leaveType });
      setSaved(true);
    } catch (e) {
      console.error(e);
    }
    setSaving(false);
  };

  const selectedPerson = people.find(p => p.id === personId);

  return (
    <Modal title="Pro-Rata Holiday Calculator" onClose={onClose}
      footer={
        <div className="flex gap-2 justify-end w-full">
          <button onClick={onClose} className="px-3 py-1 text-[10px] font-semibold border border-gray-200 rounded bg-white">Close</button>
          {result && !saved && (
            <button onClick={handleSave} disabled={saving} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">
              {saving ? 'Saving…' : 'Apply & Save Allowance'}
            </button>
          )}
        </div>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2">
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Employee *</label>
            <select className="form-select" value={personId} onChange={e => { setPersonId(e.target.value); setResult(null); setSaved(false); }}>
              <option value="">Select employee…</option>
              {people.filter(p => p.employee_status !== 'terminated').map(p => (
                <option key={p.id} value={p.id}>{p.first_name} {p.last_name} {p.designation ? `— ${p.designation}` : ''}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Leave Year *</label>
            <select className="form-select" value={year} onChange={e => { setYear(e.target.value); setResult(null); }}>
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Leave Type</label>
            <select className="form-select" value={leaveType} onChange={e => setLeaveType(e.target.value)}>
              <option value="annual">Annual Leave</option>
              <option value="sick">Sick Leave</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        {/* Person snapshot */}
        {selectedPerson && (
          <div className="bg-gray-50 border border-gray-200 rounded p-2 text-[9px] grid grid-cols-3 gap-1">
            <div><span className="text-gray-500">Join Date: </span><span className="font-semibold">{formatDate(selectedPerson.date_of_joining) || '—'}</span></div>
            <div><span className="text-gray-500">FTE: </span><span className="font-semibold">{selectedPerson.fte ?? 1.0}</span></div>
            <div><span className="text-gray-500">Entitlement: </span><span className="font-semibold">{selectedPerson.holiday_entitlement ?? 25} days</span></div>
          </div>
        )}

        <button onClick={handleCalculate} disabled={!personId || saving} className="w-full py-1.5 text-[10px] font-semibold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-40">
          {saving ? 'Calculating…' : 'Calculate'}
        </button>

        {/* Result */}
        {result && (
          <div className={`rounded p-3 border ${saved ? 'bg-emerald-50 border-emerald-300' : 'bg-blue-50 border-blue-200'}`}>
            {saved && <div className="text-[9px] font-bold text-emerald-600 mb-1">✓ Allowance saved to leave records</div>}
            <div className="grid grid-cols-2 gap-1 text-[9px]">
              <div><span className="text-gray-500">Year: </span><span className="font-semibold">{result.year}</span></div>
              <div><span className="text-gray-500">Start Date: </span><span className="font-semibold">{formatDate(result.startDate) || 'Before year start'}</span></div>
              <div><span className="text-gray-500">FTE: </span><span className="font-semibold">{result.fte}</span></div>
              <div><span className="text-gray-500">Full entitlement: </span><span className="font-semibold">{result.fullEntitlement} days</span></div>
            </div>
            <div className="mt-2 text-center">
              <div className="text-[8px] text-gray-500 uppercase">Pro-Rated Allowance</div>
              <div className="text-2xl font-extrabold text-navy">{result.proRatedDays} <span className="text-sm font-normal text-gray-400">days</span></div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

// The kinds of leave a request can be moved between. Matches what the server
// stores and what My Info offers, so a correction cannot land on a type the
// allowance tables have never heard of.
const EDITABLE_LEAVE_TYPES = [
  { value: 'annual',           label: 'Annual Leave' },
  { value: 'carry_over',       label: 'Carried Over' },
  { value: 'tenure_annual',    label: 'Long Service' },
  { value: 'benefits_annual',  label: 'Benefits Leave' },
  { value: 'wellbeing',        label: 'Life Admin / Wellbeing Day' },
  { value: 'sick',             label: 'Sick Leave' },
  { value: 'company_training', label: 'Company Training' },
  { value: 'toil',             label: 'TOIL' },
  { value: 'other',            label: 'Other' },
];

// ── Recording an absence for somebody in your team ───────────────────────────
//
// Leave could only be REQUESTED, by the person it belonged to, so a manager
// whose engineer rang in ill had nothing to enter it with and the planner had
// them at a desk all week. This is the other half of that: a manager states
// what happened.
//
// It is not a request and does not pretend to be. It saves already approved,
// stamped to whoever entered it, and the footer says so, because a dialog that
// looked like the request form would leave somebody waiting for an approval
// step that never comes. The person is emailed either way.
//
// The day count comes from utils/leaveDays, the same function My Leave uses.
// Item 29's warning was about exactly this: a second form counting days its own
// way produced figures the first one never would.
function RecordAbsenceModal({ onClose, onSaved }) {
  const [people, setPeople] = useState([]);
  const [bankHolidays, setBankHolidays] = useState(UK_BANK_HOLIDAYS_FALLBACK);
  const [form, setForm] = useState({
    person_id: '', leave_type: 'sick', start_date: '', end_date: '',
    start_half: 'full', end_half: 'full', reason: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  useEffect(() => { api.get('leave-requests/recordable-people').then(setPeople).catch(() => {}); }, []);
  useEffect(() => {
    api.get('bank-holidays').then(rows => {
      if (Array.isArray(rows) && rows.length) setBankHolidays(new Set(rows.map(r => (r.holiday_date || '').slice(0, 10))));
    }).catch(() => {});
  }, []);

  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setError(''); };
  const person = people.find(p => p.id === form.person_id);
  const days = leaveDaysFor({
    start: form.start_date, end: form.end_date, fte: person?.fte || 1, workingDays: person?.working_days,
    bankHolidays, startHalf: form.start_half, endHalf: form.end_half,
  });

  const save = async () => {
    if (!form.person_id) { setError('Choose whose absence this is.'); return; }
    if (!form.start_date || !form.end_date) { setError('Give the dates it covers.'); return; }
    if (days === '') { setError('There are no working days in those dates.'); return; }
    setSaving(true); setError('');
    try {
      await api.post('leave-requests', { ...form, days });
      onSaved();
      onClose();
    } catch (e) {
      setError(e.message || 'The absence could not be recorded.');
    }
    setSaving(false);
  };

  return (
    <Modal title="Record an absence" onClose={onClose}
      footer={
        <div className="flex items-center justify-between gap-2 w-full">
          <span className="text-[9px] text-gray-400">
            This goes straight onto their record with no approval step, and {person ? person.first_name : 'the person'} is emailed a copy.
          </span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1 text-[10px] font-semibold border border-gray-200 rounded bg-white">Cancel</button>
            <button onClick={save} disabled={saving} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">
              {saving ? 'Recording…' : 'Record absence'}
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2">
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Who was absent</label>
            <select className="form-select" value={form.person_id} onChange={e => set('person_id', e.target.value)}>
              <option value="">Select somebody…</option>
              {people.map(p => (
                <option key={p.id} value={p.id}>{p.first_name} {p.last_name}{p.designation ? ` — ${p.designation}` : ''}</option>
              ))}
            </select>
            {!people.length && (
              <div className="text-[9px] text-gray-500 mt-1">Nobody reports to you, so there is nobody to record an absence for.</div>
            )}
          </div>
          <div className="col-span-2">
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Type</label>
            <select className="form-select" value={form.leave_type} onChange={e => set('leave_type', e.target.value)}>
              {EDITABLE_LEAVE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">From</label>
            <input type="date" className="form-input" value={form.start_date} onChange={e => set('start_date', e.target.value)} />
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">To</label>
            <input type="date" className="form-input" value={form.end_date} onChange={e => set('end_date', e.target.value)} />
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">First day</label>
            <select className="form-select" value={form.start_half} onChange={e => set('start_half', e.target.value)}>
              <option value="full">All day</option>
              <option value="pm">Afternoon only</option>
            </select>
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Last day</label>
            <select className="form-select" value={form.end_half} onChange={e => set('end_half', e.target.value)}>
              <option value="full">All day</option>
              <option value="am">Morning only</option>
            </select>
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Days</label>
            {/* Reported, never typed: the server counts it the same way, and a
                figure typed over was only ever going to disagree with the
                allowance it was charged against. */}
            <input className="form-input bg-gray-50" value={days === '' ? '—' : days} readOnly tabIndex={-1} />
          </div>
          <div className="col-span-2">
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Note (optional)</label>
            <input className="form-input" value={form.reason} onChange={e => set('reason', e.target.value)}
              placeholder="Rang in unwell" />
          </div>
        </div>
        {/* At the control, not in a banner at the top of the page: the person
            is looking at this dialog when it fails. */}
        {error && <div className="text-[10px] text-red-600 font-semibold">{error}</div>}
      </div>
    </Modal>
  );
}

// ── Amending a request that has already been decided (HR and admin) ──────────
//
// Before this, a decided request was frozen: a holiday booked against the wrong
// type, or for the wrong week, could only be put right by cancelling it and
// asking the person to file it again, which left a cancelled twin in the
// register for every correction.
//
// The server does the arithmetic — it revalidates the period against bank
// holidays, company closures and the allowance, then moves the booked days to
// whichever allowance they now belong to. This dialog's job is to send the
// change and to show the refusal where the person pressed the button, rather
// than in a panel at the top of a page they are not looking at.
function EditLeaveModal({ request, onClose, onSaved }) {
  const [form, setForm] = useState({
    leave_type: request.leave_type || 'annual',
    start_date: (request.start_date || '').slice(0, 10),
    end_date:   (request.end_date || '').slice(0, 10),
    days:       String(request.days ?? ''),
    start_half: request.start_half || 'full',
    end_half:   request.end_half || 'full',
    reason:     request.reason || '',
    status:     request.status,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setError(''); };

  const save = async () => {
    setSaving(true); setError('');
    try {
      await api.put(`leave-requests/${request.id}`, { ...form, days: parseFloat(form.days) || 0 });
      onSaved();
      onClose();
    } catch (e) {
      setError(e.message || 'The change could not be saved.');
    }
    setSaving(false);
  };

  return (
    <Modal title={`Amend leave — ${request.person_name}`} onClose={onClose}
      footer={
        <div className="flex items-center justify-between gap-2 w-full">
          <span className="text-[9px] text-gray-400">The allowance is adjusted to match, and {request.person_name?.split(' ')[0] || 'the person'} is told what changed.</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1 text-[10px] font-semibold border border-gray-200 rounded bg-white">Close</button>
            <button onClick={save} disabled={saving} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2">
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Leave type</label>
            <select className="form-select" value={form.leave_type} onChange={e => set('leave_type', e.target.value)}>
              {EDITABLE_LEAVE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">From</label>
            <input type="date" className="form-input" value={form.start_date} onChange={e => set('start_date', e.target.value)} />
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">To</label>
            <input type="date" className="form-input" value={form.end_date} onChange={e => set('end_date', e.target.value)} />
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">First day</label>
            <select className="form-select" value={form.start_half} onChange={e => set('start_half', e.target.value)}>
              <option value="full">All day</option>
              <option value="pm">Afternoon only</option>
            </select>
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Last day</label>
            <select className="form-select" value={form.end_half} onChange={e => set('end_half', e.target.value)}>
              <option value="full">All day</option>
              <option value="am">Morning only</option>
            </select>
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Days</label>
            <input type="number" step="0.5" min="0.5" className="form-input" value={form.days} onChange={e => set('days', e.target.value)} />
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Status</label>
            <select className="form-select" value={form.status} onChange={e => set('status', e.target.value)}>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
              <option value="pending">Pending</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Reason</label>
            <input type="text" className="form-input" value={form.reason} onChange={e => set('reason', e.target.value)} placeholder="Optional" />
          </div>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded p-2 text-[9px] text-gray-500">
          Currently {request.days}d of {request.leave_type?.replace(/_/g, ' ')} from {formatDate(request.start_date)} to {formatDate(request.end_date)}, {request.status}.
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded p-2 text-[10px] text-red-700">{error}</div>
        )}
      </div>
    </Modal>
  );
}

// The leave calendar (approved leave, org-wide) is its own page,
// LeaveCalendarPage, open to every internal role. This page is the requests
// and approvals view for line managers, HR and admin.

// `embedded` is set when this is a tab of the Approvals page, which has
// already said what the screen is. The controls stay; only the heading goes.
export default function HolidaysPage({ embedded = false }) {
  const { currentUser } = useApp();
  const { toast } = useToast();
  const userRoles = parseRoles(currentUser?.user_role);
  const isHR = userRoles.some(r => ['admin', 'hr'].includes(r));
  const [requests, setRequests]     = useState([]);
  const [showProRata, setShowProRata] = useState(false);
  const [recording, setRecording]   = useState(false);
  const [people, setPeople]         = useState([]);
  const [mainTab, setMainTab]       = useState('requests'); // requests | by-person
  const [reqSubTab, setReqSubTab]   = useState('pending'); // pending | processed
  const [drillPerson, setDrillPerson] = useState(null); // person_id when drilling down
  const [editing, setEditing]       = useState(null); // the processed request HR is amending

  const load = () => { api.get('leave-requests').then(setRequests); };
  useEffect(load, []);
  useEffect(() => { if (isHR) api.get('people').then(setPeople).catch(() => {}); }, [isHR]);

  const today = new Date().toISOString().split('T')[0];

  // WHOSE REQUESTS THIS SCREEN SHOWS.
  //
  // The server answers this per row. is_report marks a person in the caller's
  // downline (their reports, their reports' reports, and so on), and
  // can_decide says the caller may approve or reject it: the requester's
  // management line, HR or an admin, and never their own. The screen draws
  // from those flags rather than re-deriving the rule, so who may decide is
  // written once, on the server. Holding PM or PMO is not on its own an
  // approval right (El's items 8 and 25); being somebody's manager is.
  //
  // A manager sees their own requests and their team's. HR and admin keep
  // sight of the whole register: they administer allowances, pro-rata and the
  // payroll end of it. Everybody else sees their own.
  const canApproveOne = (r) => !!r.can_decide;
  const isTeamManager = requests.some(r => r.is_report);
  const showTeam = isHR || isTeamManager;
  const visibleRequests = isHR
    ? requests
    : requests.filter(r => r.person_id === currentUser?.id || r.is_report);
  const teamRequests = isHR ? requests : requests.filter(r => r.is_report);

  // The approval queue: requests THIS person may decide, not every pending
  // request in the company. The server never flags your own as decidable.
  const pending = requests.filter(r => r.status === 'pending' && canApproveOne(r));
  // Changes somebody has asked for to leave already approved. The leave stays
  // booked as approved until this is decided, and a rejection leaves it so.
  const changes = requests.filter(r => r.status === 'approved' && proposedChange(r) && canApproveOne(r));

  // Away today is org-wide for everyone
  const approved = requests.filter(r => r.status === 'approved');
  const awayToday = approved.filter(r => r.start_date <= today && r.end_date >= today);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`leave-requests/${id}`, { status, approval_date: new Date().toISOString() });
      load();
    } catch (e) {
      toast.error('Failed to update status: ' + (e.message || 'Unknown error'));
    }
  };

  const decideChange = async (r, decision) => {
    try {
      await api.post(`leave-requests/${r.id}/change/decision`, { decision });
      load();
    } catch (e) {
      toast.error('Failed to decide the change: ' + (e.message || 'Unknown error'));
    }
  };

  // Your own leave is yours to withdraw until the day it starts, whether or not
  // it has been approved. The server holds the same rule; this stops the button
  // being offered for something that would only be refused.
  const canCancelOwn = (r) =>
    (r.status === 'pending' || r.status === 'approved') && (r.start_date || '').slice(0, 10) > today;

  const cancelRequest = async (r) => {
    const wasApproved = r.status === 'approved';
    if (!window.confirm(wasApproved
      ? `Cancel this approved leave? Its ${r.days} day${r.days === 1 ? '' : 's'} go back to the allowance.`
      : 'Cancel this leave request?')) return;
    try {
      await api.put(`leave-requests/${r.id}`, { status: 'cancelled' });
      load();
    } catch (e) { toast.error('Failed to cancel: ' + e.message); }
  };

  return (
    <div className="fade-in">
      <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
        <div>
          {!embedded && <h2 className="text-base font-extrabold text-navy">Holiday & Absence Requests</h2>}
          <div className="text-[9px] text-gray-500">
            {isHR ? 'Showing all staff requests' : isTeamManager ? "Showing your requests and your team's" : 'Showing your requests'}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {isHR && (
            <button onClick={() => setShowProRata(true)} className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80">⟳ Pro-Rata Calc</button>
          )}
          {/* Recording an absence for somebody else is a manager's job as much
              as HR's, so this follows showTeam, the same test the team view
              uses, rather than the HR-only test the pro-rata calculator uses.
              Who it may be used FOR is decided on the server. */}
          {showTeam && (
            <button data-tour="record-absence" onClick={() => setRecording(true)} className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80">+ Record absence</button>
          )}
          {/* Item 29: "Second place to request leave unnecessary." Leave was
              requestable here AND on My Info, with different forms — this one did
              not know the requester's FTE, so the working-day count it filled in
              could differ from the one My Info produced for the same dates.
              Requesting now happens in one place, next to your own allowance and
              your own balance, which is the only place it can be checked against
              anything. */}
          {/* A note, not a button. "Make this a note style, not a button as the button is
              clickable but leads to nowhere" — and it led nowhere for a reason worth
              recording: this app navigates with pushState and listens for popstate, so a
              plain href="#page" changed the address bar and nothing else. The listener is
              fixed too (App.jsx), but a pointer to another screen should not look like an
              action in the first place. */}
          <span className="text-[10px] text-gray-500 italic">
            Leave is requested from My Space → My Info, where your own allowance and balance are.
          </span>
        </div>
      </div>

      {/* Main tabs */}
      <div className="flex gap-1 mb-3">
        {[
          ['requests', 'Requests'],
          ...(showTeam ? [['by-person', 'By Person']] : []),
        ].map(([key, label]) => (
          <button key={key} onClick={() => { setMainTab(key); setDrillPerson(null); }}
              className={`px-3 py-1 text-[10px] font-semibold rounded ${mainTab === key ? 'bg-navy text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              {label}
            </button>
          ))}
        </div>

      {/* By Person tab: HR sees everybody, a manager sees their team */}
      {mainTab === 'by-person' && showTeam && (() => {
        // Build per-person summary from the requests this person oversees
        const byPerson = {};
        teamRequests.forEach(r => {
          if (!byPerson[r.person_id]) {
            byPerson[r.person_id] = {
              id: r.person_id, name: r.person_name,
              avatar_color: r.avatar_color,
              pending: 0, approved: 0, rejected: 0, total: 0,
            };
          }
          byPerson[r.person_id].total++;
          if (r.status === 'pending')  byPerson[r.person_id].pending++;
          if (r.status === 'approved') byPerson[r.person_id].approved++;
          if (r.status === 'rejected') byPerson[r.person_id].rejected++;
        });
        const personList = Object.values(byPerson).sort((a, b) => b.pending - a.pending || a.name?.localeCompare(b.name));
        const drillReqs  = drillPerson ? teamRequests.filter(r => r.person_id === drillPerson) : [];
        const drillInfo  = byPerson[drillPerson];

        return (
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-3">
            {/* Left: people list */}
            <div className="bg-white border border-gray-200 rounded overflow-hidden">
              <div className="px-3 py-2 border-b bg-gray-50 text-[8px] font-bold text-gray-500 uppercase">
                {personList.length} people with requests
              </div>
              <div className="overflow-y-auto max-h-[calc(100vh-250px)]">
                {personList.map(p => (
                  <button key={p.id}
                    onClick={() => setDrillPerson(p.id === drillPerson ? null : p.id)}
                    className={`w-full text-left flex items-center gap-2 px-3 py-2 border-b border-gray-100 hover:bg-gray-50 transition-colors ${drillPerson === p.id ? 'bg-navy/5 border-l-2 border-l-navy' : ''}`}
                  >
                    <Avatar personId={p.id} className="w-7 h-7 rounded-full flex items-center justify-center text-[8px] font-bold text-white flex-shrink-0"
                      style={{ background: p.avatar_color || '#6B7280' }}>
                      {p.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="text-[9px] font-semibold truncate">{p.name}</div>
                      <div className="flex gap-1.5 mt-0.5">
                        {p.pending > 0 && <span className="text-[7px] font-bold text-amber-600">{p.pending} pending</span>}
                        <span className="text-[7px] text-gray-400">{p.approved} approved</span>
                        <span className="text-[7px] text-gray-400">{p.total} total</span>
                      </div>
                    </div>
                    {p.pending > 0 && (
                      <span className="w-4 h-4 rounded-full bg-amber-500 text-amber-950 text-[7px] font-bold flex items-center justify-center flex-shrink-0">{p.pending}</span>
                    )}
                  </button>
                ))}
                {personList.length === 0 && <div className="p-6 text-center text-[10px] text-gray-400">No leave requests yet</div>}
              </div>
            </div>

            {/* Right: drill-down or prompt */}
            <div>
              {!drillPerson ? (
                <div className="flex items-center justify-center h-48 bg-white border border-gray-200 rounded text-[10px] text-gray-400">
                  Select a person on the left to view their requests
                </div>
              ) : (
                <div className="bg-white border border-gray-200 rounded overflow-hidden">
                  <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50">
                    <div className="flex items-center gap-2">
                      <Avatar personId={drillPerson} className="w-7 h-7 rounded-full flex items-center justify-center text-[8px] font-bold text-white"
                        style={{ background: drillInfo?.avatar_color || '#6B7280' }}>
                        {drillInfo?.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </Avatar>
                      <div>
                        <div className="text-[10px] font-bold">{drillInfo?.name}</div>
                        <div className="text-[8px] text-gray-400">{drillReqs.length} requests · {drillInfo?.approved} approved · {drillInfo?.pending} pending</div>
                      </div>
                    </div>
                    <button onClick={() => setDrillPerson(null)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
                  </div>
                  <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead><tr>
                      <th className="px-3 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Type</th>
                      <th className="px-3 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Dates</th>
                      <th className="px-3 py-1.5 text-center text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Days</th>
                      <th className="px-3 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Status</th>
                      <th className="px-3 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Reason</th>
                      <th className="px-3 py-1.5 border-b bg-gray-50" />
                    </tr></thead>
                    <tbody>
                      {drillReqs.sort((a, b) => b.start_date.localeCompare(a.start_date)).map(r => (
                        <tr key={r.id} className="hover:bg-gray-50 border-b border-gray-100">
                          <td className="px-3 py-1.5"><Tag status={r.leave_type} /></td>
                          <td className="px-3 py-1.5 text-[9px] whitespace-nowrap">{formatDate(r.start_date)}{r.start_half === 'pm' ? ' (pm)' : ''} – {formatDate(r.end_date)}{r.end_half === 'am' ? ' (am)' : ''}</td>
                          <td className="px-3 py-1.5 text-[10px] font-semibold text-center">{r.days}</td>
                          <td className="px-3 py-1.5"><Tag status={r.status} /></td>
                          <td className="px-3 py-1.5 text-[9px] text-gray-400 max-w-[180px] truncate">{r.reason || '—'}</td>
                          <td className="px-3 py-1.5 text-right whitespace-nowrap">
                            {r.status === 'pending' && canApproveOne(r) && r.person_id !== currentUser?.id && (
                              <div className="flex gap-1 justify-end">
                                <button onClick={() => updateStatus(r.id, 'approved')} className="px-1.5 py-0.5 text-[7px] font-semibold bg-emerald-500 text-emerald-950 rounded">Approve</button>
                                <button onClick={() => updateStatus(r.id, 'rejected')} className="px-1.5 py-0.5 text-[7px] font-semibold bg-red-500 text-white rounded">Reject</button>
                              </div>
                            )}
                            {/* Same control, same gate and same dialog as Requests -> Processed,
                                so HR can correct a record from whichever view they found it in. */}
                            {isHR && r.status !== 'pending' && (
                              <button onClick={() => setEditing(r)} className="text-[8px] text-navy hover:text-gold font-semibold">Edit</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>{/* end overflow-x-auto */}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Requests tab */}
      {mainTab === 'requests' && <div data-tour="leave-queue">

      {/* Stats row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
        {showTeam ? (
          <div className={`bg-white border rounded p-2 cursor-pointer ${pending.length + changes.length ? 'border-amber-300 bg-amber-50' : 'border-gray-200'}`} onClick={() => setReqSubTab('pending')}>
            <div className={`text-[8px] uppercase font-bold ${pending.length + changes.length ? 'text-amber-600' : 'text-gray-500'}`}>Pending Approval</div>
            <div className={`text-lg font-extrabold ${pending.length + changes.length ? 'text-amber-500' : 'text-navy'}`}>{pending.length + changes.length}</div>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded p-2">
            <div className="text-[8px] text-gray-500 uppercase font-bold">My Pending</div>
            <div className="text-lg font-extrabold text-navy">{visibleRequests.filter(r => r.status === 'pending').length}</div>
          </div>
        )}
        <div className="bg-white border border-gray-200 rounded p-2">
          <div className="text-[8px] text-gray-500 uppercase font-bold">Away Today</div>
          <div className="text-lg font-extrabold text-navy">{awayToday.length}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded p-2">
          <div className="text-[8px] text-gray-500 uppercase font-bold">{isHR ? 'Total Requests' : isTeamManager ? 'Team Requests' : 'My Requests'}</div>
          <div className="text-lg font-extrabold text-navy">{visibleRequests.length}</div>
        </div>
      </div>

      {/* Away today — visible to everyone (org-wide) */}
      {awayToday.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-3">
          <h4 className="text-[9px] font-bold text-blue-700 mb-1">Away Today — Organisation</h4>
          <div className="flex flex-wrap gap-2">{awayToday.map(r => (
            <div key={r.id} className="flex items-center gap-1.5 bg-white px-2 py-1 rounded border border-blue-200">
              <Avatar personId={r.person_id} className="w-4 h-4 rounded-full flex items-center justify-center text-[6px] font-bold text-white" style={{ background: r.avatar_color || '#3B82F6' }}>{r.person_name?.charAt(0)}</Avatar>
              <span className="text-[9px] font-semibold">{r.person_name}</span>
              <Tag status={r.leave_type} />
            </div>
          ))}</div>
        </div>
      )}

      {/* Pending / Processed sub-tabs */}
      <div className="flex gap-1 mb-3 border-b border-gray-200">
        {[
          ['pending',   `Pending (${visibleRequests.filter(r => r.status === 'pending').length + changes.length})`],
          ['processed', `Processed (${visibleRequests.filter(r => r.status !== 'pending').length})`],
        ].map(([key, label]) => (
          <button key={key} onClick={() => setReqSubTab(key)}
            className={`px-3 py-1.5 text-[10px] font-semibold border-b-2 -mb-px transition-colors ${reqSubTab === key ? 'border-navy text-navy' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Pending approvals: what this person may decide */}
      {reqSubTab === 'pending' && pending.length > 0 && (
        <div className="bg-white border border-amber-200 rounded mb-3">
          <div className="px-3 py-2 border-b border-amber-200 bg-amber-50">
            <h3 className="text-[10px] font-bold text-amber-700">Pending Approvals ({pending.length})</h3>
          </div>
          <div className="overflow-x-auto">
          <table className="w-full"><thead><tr>
            <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Person</th>
            <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Type</th>
            <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Dates</th>
            <th className="px-2 py-1 text-center text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Days</th>
            <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Also on leave these dates</th>
            <th className="px-2 py-1 text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50"></th>
          </tr></thead>
          <tbody>{pending.map(r => {
            const concurrent = approved.filter(a =>
              a.person_id !== r.person_id &&
              a.start_date <= r.end_date &&
              a.end_date >= r.start_date
            );
            return (
              <tr key={r.id} className="hover:bg-amber-50/50 align-top">
                <td className="px-2 py-1 text-[10px] font-semibold">{r.person_name}</td>
                <td className="px-2 py-1"><Tag status={r.leave_type} /></td>
                <td className="px-2 py-1 text-[9px] whitespace-nowrap">{formatDate(r.start_date)}{r.start_half === 'pm' ? ' (pm)' : ''} – {formatDate(r.end_date)}{r.end_half === 'am' ? ' (am)' : ''}</td>
                <td className="px-2 py-1 text-[10px] font-semibold text-center">{r.days}</td>
                <td className="px-2 py-1">
                  {concurrent.length === 0
                    ? <span className="text-[8px] text-gray-300">None</span>
                    : (
                      <div className="flex flex-wrap gap-1">
                        {concurrent.map(a => (
                          <div key={a.id} className="flex items-center gap-1 bg-blue-50 border border-blue-100 rounded px-1.5 py-0.5" title={`${a.start_date} – ${a.end_date} (${a.leave_type})`}>
                            <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: a.avatar_color || '#3B82F6' }} />
                            <span className="text-[8px] font-semibold text-blue-700">{a.person_name}</span>
                            <Tag status={a.leave_type} />
                          </div>
                        ))}
                      </div>
                    )
                  }
                </td>
                <td className="px-2 py-1"><div className="flex gap-1">
                  <button onClick={() => updateStatus(r.id, 'approved')} className="px-2 py-0.5 text-[8px] font-semibold bg-emerald-500 text-emerald-950 rounded hover:bg-emerald-600">Approve</button>
                  <button onClick={() => updateStatus(r.id, 'rejected')} className="px-2 py-0.5 text-[8px] font-semibold bg-red-500 text-white rounded hover:bg-red-600">Reject</button>
                </div></td>
              </tr>
            );
          })}</tbody></table>
          </div>{/* end overflow-x-auto */}
        </div>
      )}

      {/* Changes asked for to approved leave: what this person may decide */}
      {reqSubTab === 'pending' && changes.length > 0 && (
        <div className="bg-white border border-amber-200 rounded mb-3">
          <div className="px-3 py-2 border-b border-amber-200 bg-amber-50">
            <h3 className="text-[10px] font-bold text-amber-700">Change Requests ({changes.length})</h3>
            <div className="text-[8px] text-amber-700">The approved leave stays booked as it is until you decide. Rejecting a change keeps it.</div>
          </div>
          <div className="overflow-x-auto">
          <table className="w-full"><thead><tr>
            <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Person</th>
            <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Approved</th>
            <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Asking for</th>
            <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Also on leave then</th>
            <th className="px-2 py-1 text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50"></th>
          </tr></thead>
          <tbody>{changes.map(r => {
            const c = proposedChange(r);
            const concurrent = approved.filter(a =>
              a.person_id !== r.person_id && a.start_date <= c.end_date && a.end_date >= c.start_date);
            return (
              <tr key={r.id} className="hover:bg-amber-50/50 align-top">
                <td className="px-2 py-1 text-[10px] font-semibold">{r.person_name}</td>
                <td className="px-2 py-1 text-[9px] whitespace-nowrap text-gray-500">
                  <Tag status={r.leave_type} /> {formatDate(r.start_date)}{r.start_half === 'pm' ? ' (pm)' : ''} – {formatDate(r.end_date)}{r.end_half === 'am' ? ' (am)' : ''}, {r.days}d
                </td>
                <td className="px-2 py-1 text-[9px] whitespace-nowrap font-semibold">
                  <Tag status={c.leave_type} /> {formatDate(c.start_date)}{c.start_half === 'pm' ? ' (pm)' : ''} – {formatDate(c.end_date)}{c.end_half === 'am' ? ' (am)' : ''}, {c.days}d
                </td>
                <td className="px-2 py-1">
                  {concurrent.length === 0
                    ? <span className="text-[8px] text-gray-300">None</span>
                    : (
                      <div className="flex flex-wrap gap-1">
                        {concurrent.map(a => (
                          <span key={a.id} className="text-[8px] font-semibold text-blue-700 bg-blue-50 border border-blue-100 rounded px-1.5 py-0.5">{a.person_name}</span>
                        ))}
                      </div>
                    )}
                </td>
                <td className="px-2 py-1"><div className="flex gap-1">
                  <button onClick={() => decideChange(r, 'approved')} className="px-2 py-0.5 text-[8px] font-semibold bg-emerald-500 text-emerald-950 rounded hover:bg-emerald-600 whitespace-nowrap">Approve change</button>
                  <button onClick={() => decideChange(r, 'rejected')} className="px-2 py-0.5 text-[8px] font-semibold bg-red-500 text-white rounded hover:bg-red-600 whitespace-nowrap">Reject change</button>
                </div></td>
              </tr>
            );
          })}</tbody></table>
          </div>
        </div>
      )}

      {/* Requests table — filtered by sub-tab */}
      {(() => {
        const tableRows = reqSubTab === 'pending'
          ? visibleRequests.filter(r => r.status === 'pending')
          : visibleRequests.filter(r => r.status !== 'pending');
        return (
          <div className="bg-white border border-gray-200 rounded">
            <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-[10px] font-bold">{reqSubTab === 'pending'
                ? (isHR ? 'All Pending Requests' : isTeamManager ? 'Team Pending Requests' : 'My Pending Requests')
                : (isHR ? 'Processed Requests' : isTeamManager ? 'Team Processed Requests' : 'My Processed Requests')}</h3>
              <span className="text-[8px] text-gray-400">{tableRows.length} request{tableRows.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full"><thead><tr>
              {showTeam && <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Person</th>}
              <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Type</th>
              <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Dates</th>
              <th className="px-2 py-1 text-center text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Days</th>
              <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Status</th>
              {showTeam && <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Reason</th>}
              <th className="px-2 py-1 border-b bg-gray-50" />
            </tr></thead>
            <tbody>{tableRows.map(r => (
              <tr key={r.id} className="hover:bg-gold/5 border-b border-gray-100">
                {showTeam && <td className="px-2 py-1.5 text-[10px] font-semibold">{r.person_name}</td>}
                <td className="px-2 py-1.5"><Tag status={r.leave_type} /></td>
                <td className="px-2 py-1.5 text-[9px] whitespace-nowrap">{formatDate(r.start_date)}{r.start_half === 'pm' ? ' (pm)' : ''} – {formatDate(r.end_date)}{r.end_half === 'am' ? ' (am)' : ''}</td>
                <td className="px-2 py-1.5 text-[10px] font-semibold text-center">{r.days}</td>
                <td className="px-2 py-1.5"><Tag status={r.status} /></td>
                {showTeam && <td className="px-2 py-1.5 text-[9px] text-gray-400 max-w-[160px] truncate">{r.reason || '—'}</td>}
                <td className="px-2 py-1.5 text-right whitespace-nowrap">
                  {/* Cancelling your own leave follows the same rule as My Info:
                      yours to withdraw until the day it starts, approved or not. */}
                  {r.person_id === currentUser?.id && canCancelOwn(r) && (
                    <button onClick={() => cancelRequest(r)} className="text-[8px] text-red-400 hover:text-red-600 font-medium">Cancel</button>
                  )}
                  {isHR && r.status !== 'pending' && (
                    <button onClick={() => setEditing(r)} className="ml-2 text-[8px] text-navy hover:text-gold font-semibold">Edit</button>
                  )}
                </td>
              </tr>
            ))}{tableRows.length === 0 && <tr><td colSpan={showTeam ? 7 : 5} className="text-center py-6 text-[10px] text-gray-400">
              {reqSubTab === 'pending' ? 'No pending requests' : 'No processed requests yet'}
            </td></tr>}</tbody></table>
            </div>{/* end overflow-x-auto */}
          </div>
        );
      })()}

      </div> /* end requests tab */}

      {recording && <RecordAbsenceModal onClose={() => setRecording(false)} onSaved={load} />}
      {showProRata && <ProRataModal people={people} onClose={() => setShowProRata(false)} />}
      {editing && <EditLeaveModal request={editing} onClose={() => setEditing(null)} onSaved={load} />}

    </div>
  );
}
